import { z } from 'zod';
import { WITHDRAWAL_SYMPTOM, withdrawalSymptomSchema, type WithdrawalSymptom } from './schema';

/**
 * The symptoms a member can report on the intake withdrawal check: the form's
 * vocabulary without `none`, which is the absence of a symptom rather than one.
 */
export const REPORTED_WITHDRAWAL_SYMPTOM = WITHDRAWAL_SYMPTOM.filter(
  (code): code is Exclude<WithdrawalSymptom, 'none'> => code !== 'none',
);
export const reportedWithdrawalSymptomSchema = withdrawalSymptomSchema.exclude(['none']);
export type ReportedWithdrawalSymptom = z.infer<typeof reportedWithdrawalSymptomSchema>;

/**
 * Read a stored `withdrawal_symptoms` answer into the symptoms it reports.
 *
 * The stored value is member-written, so a later vocabulary or a hand-edited
 * row can hold anything: values outside the vocabulary are dropped rather than
 * rejected, `none` is dropped, duplicates collapse, and the result follows the
 * form's order rather than the answer's, so two readers of the same answer show
 * it alike. Anything that is not an array reads as no symptoms.
 */
export function readReportedWithdrawalSymptoms(value: unknown): ReportedWithdrawalSymptom[] {
  if (!Array.isArray(value)) return [];
  const selected = new Set<ReportedWithdrawalSymptom>();
  for (const entry of value) {
    const parsed = reportedWithdrawalSymptomSchema.safeParse(entry);
    if (parsed.success) selected.add(parsed.data);
  }
  return REPORTED_WITHDRAWAL_SYMPTOM.filter((code) => selected.has(code));
}
